import axios from 'axios';

const API_URL = "https://seltentechnologiesdemo.com/moona-backend/service/v4_1/rest.php";
const INPUT_TYPE = "JSON";
const RESPONSE_TYPE = "JSON";

// Fields exposed in the Address UI and available in the SuiteCRM module
const ADDRESS_MODULE_FIELDS = [
  "id", // Required for edit operations
  "parentid",
  "addresstype_c",
  "address",
  "street",
  "pincode",
  "village",
  "taluk",
  "district",
  "state",
  "country",
  "date_entered",
  "date_modified",
  "deleted",
];

// Helper function to process address data entries
const processAddressData = (entryList) => {
  if (!Array.isArray(entryList)) {
    console.log("⚠️ [ADDRESS SERVICE] entryList is not an array:", entryList);
    return [];
  }

  console.log("📦 [ADDRESS SERVICE] Processing", entryList.length, "entries");

  return entryList.map((entry) => {
    const values = Object.fromEntries(
      Object.values(entry.name_value_list || {}).map((field) => [field.name, field.value])
    );

    console.log("📦 [ADDRESS SERVICE] Raw entry values:", values);

    const filteredValues = {};
    ADDRESS_MODULE_FIELDS.forEach((field) => {
      if (values[field] !== undefined && values[field] !== null) {
        filteredValues[field] = values[field];
      }
    });

    const processedEntry = {
      id: entry.id,
      ...filteredValues,
    };

    console.log("✅ [ADDRESS SERVICE] Processed entry:", processedEntry);

    return processedEntry;
  });
};

/**
 * Fetch all address data from Address module
 * @param {string} sessionId - SuiteCRM session ID
 * @returns {Promise<Array>} - Array of address objects
 */
export const fetchAddressData = async (sessionId) => {
  try {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: "add_Address",
      select_fields: ADDRESS_MODULE_FIELDS,
    });

    const fullUrl = `${API_URL}?method=get_entry_list&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.get(fullUrl);
    const entryList = response?.data?.entry_list || [];

    return processAddressData(entryList);
  } catch (error) {
    console.error('Error fetching address data:', error);
    throw new Error('Failed to fetch address data');
  }
};

/**
 * Fetch address entries for a specific parent/user
 * @param {string} sessionId
 * @param {string} parentId
 * @returns {Promise<Array>} - Array of address entries associated with the parent
 */
export const fetchAddressDetailsByParent = async (sessionId, parentId) => {
  if (!sessionId || !parentId) {
    console.log("⚠️ [ADDRESS SERVICE] Missing sessionId or parentId");
    return [];
  }

  try {
    console.log("🔍 [ADDRESS SERVICE] Fetching addresses for parentId:", parentId);
    
    // Try simple approach - get all addresses and filter on client side
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: "add_Address",
      query: "",
      order_by: "",
      offset: 0,
      select_fields: ADDRESS_MODULE_FIELDS,
      max_results: 1000,
      deleted: 0
    });

    const fullUrl = `${API_URL}?method=get_entry_list&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;
    
    console.log("🌐 [ADDRESS SERVICE] API URL:", fullUrl);
    
    const response = await axios.get(fullUrl);
    
    console.log("📡 [ADDRESS SERVICE] API Response:", response.data);
    
    const entryList = response?.data?.entry_list || [];
    
    console.log("📋 [ADDRESS SERVICE] Entry list length (before filter):", entryList.length);

    const processedData = processAddressData(entryList);
    
    // Filter by parentId on client side
    const filteredData = processedData.filter(entry => entry.parentid === parentId || entry.parentid === String(parentId));
    
    console.log("📋 [ADDRESS SERVICE] Filtered entries for parentId", parentId, ":", filteredData.length);
    console.log("✅ [ADDRESS SERVICE] Returning processed data:", filteredData);
    
    return filteredData;
  } catch (error) {
    console.error('❌ [ADDRESS SERVICE] Error fetching address data by parent:', error);
    console.error('❌ [ADDRESS SERVICE] Error details:', error.response?.data || error.message);
    return [];
  }
};

/**
 * Add new address entry
 * @param {string} sessionId - SuiteCRM session ID
 * @param {Object} data - Address data object
 * @returns {Promise<Object>} - Created entry data
 */
export const addAddressData = async (sessionId, data) => {
  try {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: "add_Address",
      name_value_list: Object.entries(data).map(([key, value]) => ({
        name: key,
        value: value
      }))
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.post(fullUrl);
    return response.data;
  } catch (error) {
    console.error('Error adding address data:', error);
    throw new Error('Failed to add address data');
  }
};

/**
 * Edit existing address entry
 * @param {string} sessionId - SuiteCRM session ID
 * @param {string} id - Entry ID to update
 * @param {Object} data - Updated address data object
 * @returns {Promise<Object>} - Updated entry data
 */
export const editAddressData = async (sessionId, id, data) => {
  try {
    // Include the id in the data object
    const dataWithId = { ...data, id };
    
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: "add_Address",
      name_value_list: Object.entries(dataWithId).map(([key, value]) => ({
        name: key,
        value: value
      }))
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.post(fullUrl);
    return response.data;
  } catch (error) {
    console.error('Error editing address data:', error);
    throw new Error('Failed to edit address data');
  }
};

/**
 * Delete address entry
 * @param {string} sessionId - SuiteCRM session ID
 * @param {string} id - Entry ID to delete
 * @returns {Promise<Object>} - Deletion result
 */
export const deleteAddressData = async (sessionId, id) => {
  try {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: "add_Address",
      name_value_list: [
        { name: "id", value: id },
        { name: "deleted", value: 1 },
      ],
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.post(fullUrl);

    if (response?.data?.id !== id) {
      console.warn(`Unexpected delete response for address entry ${id}:`, response?.data);
    }

    return response.data;
  } catch (error) {
    console.error('Error deleting address data:', error);
    throw new Error('Failed to delete address data');
  }
};
